import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pagination } from "semantic-ui-react";
import { DatatablePageSizeSelect } from "../../CompletedChats/DatatablePageSizeSelect";
import { filterCampaignCustomers, useDebouncedValue } from "../../OutboundCampaign/customers/filterCampaignCustomers";

export default function CustomerTableSMS({
  customers,
  customersPage,
  customersPerPage: customersPerPageProp = 10,
  getTotalCustomers,
  viewCustomer,
  setCustomersPage,
  setCustomers,
  interactionsEnabled = true,
  onNotify,
}) {
  const [customerSearch, setCustomerSearch] = useState("");
  const debouncedCustomerSearch = useDebouncedValue(customerSearch);
  const [customersPerPage, setCustomersPerPage] = useState(customersPerPageProp);
  const list = Array.isArray(customers) ? customers : [];
  const matched = new Set(filterCampaignCustomers(list, debouncedCustomerSearch));
  const filteredWithIndex = list
    .map((customer, originalIndex) => ({ customer, originalIndex }))
    .filter(({ customer }) => matched.has(customer));
  const totalFiltered = filteredWithIndex.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / customersPerPage) || 1);
  const activePage = Math.min(customersPage, totalPages);
  const start = (activePage - 1) * customersPerPage;
  const end = start + customersPerPage;
  const pageSlice = filteredWithIndex.slice(start, end);

  useEffect(() => {
    setCustomersPage(1);
  }, [debouncedCustomerSearch, customersPerPage, setCustomersPage]);

  useEffect(() => {
    if (customersPage > totalPages) {
      setCustomersPage(totalPages);
    }
  }, [totalPages, customersPage, setCustomersPage]);

  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const selectAllCheckboxRef = useRef(null);

  const selectableIndexesOnPage = useMemo(
    () => pageSlice.map(({ originalIndex }) => originalIndex),
    [pageSlice]
  );

  const allRowsOnPageSelected =
    selectableIndexesOnPage.length > 0 &&
    selectableIndexesOnPage.every((i) => selectedIndexes.includes(i));

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (!el) return;
    const selectedOnPage = selectableIndexesOnPage.filter((i) =>
      selectedIndexes.includes(i)
    );
    el.indeterminate =
      selectedOnPage.length > 0 &&
      selectedOnPage.length < selectableIndexesOnPage.length;
  }, [selectableIndexesOnPage, selectedIndexes]);

  const deleteDisabled = !interactionsEnabled || selectedIndexes.length === 0;

  const formatPhoneDisplay = (phone) => {
    if (!phone) return "N/A";
    const s = String(phone);
    if (s.startsWith("+")) return s;
    return s;
  };

  return (
    <>
      {getTotalCustomers() > 0 && (
        <div className="card shadow-sm p-3" style={{ marginTop: 20, width: "100%" }}>
          <h2 className="mb-3">Customers Info</h2>
          <div className="customers-info-toolbar">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="kt-input-icon kt-input-icon--right header-search">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, phone, or date..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                  }}
                  aria-label="Search customers"
                />
                <span className="kt-input-icon__icon kt-input-icon__icon--right">
                  <span><i className="la la-search" aria-hidden="true" /></span>
                </span>
              </div>
            </form>
            <div className="customers-info-toolbar-actions">
              <button
                type="button"
                className="btn btn-sm"
                disabled={deleteDisabled}
                title="Delete"
                aria-label="Delete"
                onClick={() => {
                  if (deleteDisabled || !setCustomers) return;
                  const toDelete = new Set(selectedIndexes);
                  const removedCount = selectedIndexes.length;
                  setCustomers((prev) =>
                    (Array.isArray(prev) ? prev : []).filter((_, idx) => !toDelete.has(idx))
                  );
                  setSelectedIndexes([]);
                  if (removedCount > 0) {
                    if (onNotify) onNotify(`${removedCount} contact(s) removed from the list.`);
                  }
                }}
                style={{
                  border: "1px solid #dc3545",
                  borderRadius: "5px",
                  padding: "8px 10px",
                  minWidth: "38px",
                  fontSize: "16px",
                  lineHeight: 1,
                  background: "none",
                  color: deleteDisabled ? "rgba(220, 53, 69, 0.4)" : "#dc3545",
                  cursor: deleteDisabled ? "not-allowed" : "pointer",
                  filter: deleteDisabled ? "blur(0.5px)" : "none",
                }}
              >
                <i
                  className="fa fa-trash"
                  style={{ fontSize: "25px", margin: "-5px 0px 0px 3px" }}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div
            className="table-responsive campaign-listing-table"
            style={{ height: "auto", overflowX: "auto", overflowY: "visible" }}
          >
            <table className="table table-hover" style={{ minWidth: "100%", width: "100%" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      verticalAlign: "middle",
                      textAlign: "center",
                      width: "40px",
                    }}
                  >
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allRowsOnPageSelected}
                      onChange={() => {
                        if (!interactionsEnabled) return;
                        setSelectedIndexes((prev) => {
                          const allSelected =
                            selectableIndexesOnPage.length > 0 &&
                            selectableIndexesOnPage.every((i) => prev.includes(i));
                          if (allSelected) {
                            return prev.filter((i) => !selectableIndexesOnPage.includes(i));
                          }
                          return Array.from(new Set([...prev, ...selectableIndexesOnPage]));
                        });
                      }}
                      disabled={!interactionsEnabled || selectableIndexesOnPage.length === 0}
                      aria-label="Select all contacts on this page"
                    />
                  </th>
                  <th style={{ verticalAlign: "middle", textAlign: "left", whiteSpace: "nowrap" }}>
                    Name
                  </th>
                  <th style={{ verticalAlign: "middle", textAlign: "left", whiteSpace: "nowrap" }}>
                    Phone
                  </th>
                  <th style={{ verticalAlign: "middle", textAlign: "left", whiteSpace: "nowrap" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageSlice.length > 0 ? pageSlice.map(({ customer, originalIndex }) => {
                  const checked = selectedIndexes.includes(originalIndex);
                  return (
                  <tr key={customer.id || `${customer.phone_number || "row"}-${originalIndex}`}>
                    <td
                      style={{
                        verticalAlign: "middle",
                        textAlign: "center",
                        width: "40px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (!interactionsEnabled) return;
                          setSelectedIndexes((prev) =>
                            prev.includes(originalIndex)
                              ? prev.filter((i) => i !== originalIndex)
                              : [...prev, originalIndex]
                          );
                        }}
                        disabled={!interactionsEnabled}
                        aria-label={`Select ${customer.name || "contact"} for delete`}
                      />
                    </td>
                    <td style={{ verticalAlign: "middle", textAlign: "left" }}>
                      <strong>{customer.name || "N/A"}</strong>
                    </td>
                    <td style={{ verticalAlign: "middle", textAlign: "left", fontFamily: "monospace" }}>
                      {formatPhoneDisplay(customer.phone_number)}
                    </td>
                    <td style={{ verticalAlign: "middle", textAlign: "left" }}>
                      <button
                        type="button"
                        onClick={() => viewCustomer && viewCustomer(customer)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#2786fb",
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                        title="View customer"
                      >
                        <i className="fa fa-eye" />
                      </button>
                    </td>
                  </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={4} className="text-start text-muted py-4">
                      No record found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 campaign-customers-footer">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 25 }}>
              <small className="text-muted d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
                <span className="mr-1">Show</span>
                <DatatablePageSizeSelect
                  limit={String(customersPerPage)}
                  onChangeLimit={(e, { value }) => {
                    setCustomersPerPage(Number(value) || 10);
                  }}
                />
                <span className="ml-1">entries</span>
              </small>
              <small className="text-muted">
                {totalFiltered === 0
                  ? "Showing 0 of 0"
                  : `Showing ${start + 1}-${Math.min(end, totalFiltered)} of ${totalFiltered}`}
              </small>
            </div>
            <Pagination
              size="mini"
              activePage={activePage}
              totalPages={totalPages}
              onPageChange={(e, { activePage }) => {
                if (totalFiltered > 0) setCustomersPage(activePage);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}

